/**
 * SettingsPage — Steward Admin
 * Steward account management only.
 * Championship creation has moved to ChampionshipsPage.
 */
import React, { useState, useEffect } from 'react';
import { functions } from '../services/firebase';
import { httpsCallable } from 'firebase/functions';

const setStewardRole = httpsCallable(functions, 'setStewardRole');
const createStewardAccount = httpsCallable(functions, 'createStewardAccount');

/* ══════════════════════════════════════
   STEWARD MANAGEMENT
   ══════════════════════════════════════ */
function StewardManagement() {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('steward');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('steward');
  const [creating, setCreating] = useState(false);

  const handleSetRole = async () => {
    if (!email.trim()) return;
    setLoading(true);
    try {
      await setStewardRole({ email: email.trim(), role });
      setToast({ type: 'success', msg: `${role} role set for ${email}` });
      setEmail('');
    } catch (err) {
      setToast({ type: 'error', msg: err?.message || 'Failed to set role' });
    } finally { setLoading(false); }
  };

  const handleCreateAccount = async () => {
    if (!newEmail.trim() || !newPassword) return;
    setCreating(true);
    try {
      await createStewardAccount({ email: newEmail.trim(), password: newPassword, role: newRole });
      setToast({ type: 'success', msg: `Account created for ${newEmail} with ${newRole} role` });
      setNewEmail(''); setNewPassword(''); setShowCreate(false);
    } catch (err) {
      setToast({ type: 'error', msg: err?.message || 'Failed to create account' });
    } finally { setCreating(false); }
  };

  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  return (
    <>
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3 className="card-title">Create Steward Account</h3>
          {!showCreate && <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>+ New Account</button>}
        </div>
        {showCreate ? (
          <>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="steward@whapaxxgaming.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="text" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Temporary password" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select className="form-select" value={newRole} onChange={e => setNewRole(e.target.value)} style={{ maxWidth: 200 }}>
                <option value="steward">Steward</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={handleCreateAccount} disabled={creating || !newEmail.trim() || !newPassword}>
                {creating ? 'Creating...' : 'Create Account'}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>Create a new Firebase Auth account with steward or admin role.</p>
        )}
      </div>
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3 className="card-title">Set Role for Existing User</h3></div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="user@example.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Role</label>
            <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
              <option value="steward">Steward</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <button className="btn btn-primary" onClick={handleSetRole} disabled={loading || !email.trim()}>
          {loading ? 'Setting role...' : 'Set Role'}
        </button>
      </div>
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

export default function SettingsPage() {
  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage steward accounts and roles</p>
        </div>
      </div>
      <StewardManagement />
    </div>
  );
}
